import { CompanyEnrichType } from "@/enum/company-enrich-type.enum";
import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";
import { CustomEventType } from "@/enum/custom-event.enum";
import { FundingMaType } from "@/enum/funding-ma.enum";
import { ScrapeLevel } from "@/enum/job.enum";
import { LiEventType } from "@/enum/lievent.enum";
import { ListSourceType } from "@/enum/list.enum";
import { LiPostType } from "@/enum/liprofile.enum";
import { ParallelAiType } from "@/enum/parallel-ai-type.enum";

import { getCatalogTileId } from "./tileCatalog";

/**
 * The example "chips" under the catalog search box. Each chip is a shortcut that narrows the
 * catalog to a small, curated set of list types that fit the intent — a one-to-many mapping, so a
 * single intent ("find people at an event") surfaces every relevant way to build that list
 * (LinkedIn event attendees, our scraping service, or a plain attendee upload).
 *
 * `tileIds` are computed from {@link getCatalogTileId} so they stay locked to the real catalog ids
 * (a renamed source type breaks the build here instead of silently pointing at nothing). Ids for
 * feature-gated tiles that aren't active simply won't be present in the rendered catalog, so they
 * drop out of the result naturally.
 */
export interface CatalogExample {
	key: string;
	tileIds: string[];
}

export const CATALOG_EXAMPLES: CatalogExample[] = [
	{
		key: "event",
		tileIds: [
			getCatalogTileId(ListSourceType.CSV_ENRICHMENTS, CsvEnrichmentType.PERSON),
			getCatalogTileId(ListSourceType.CSV_ENRICHMENTS, CsvEnrichmentType.ORGANIZATION),
			getCatalogTileId(ListSourceType.CSV_ENRICHMENTS, CsvEnrichmentType.PERSON_BY_TITLE),
			getCatalogTileId(ListSourceType.CUSTOM_EVENTS, CustomEventType.PERSON),
			getCatalogTileId(ListSourceType.CUSTOM_EVENTS, CustomEventType.ORGANIZATION),
			getCatalogTileId(ListSourceType.LI_EVENTS, undefined, LiEventType.SPECIFIC),
			getCatalogTileId(ListSourceType.LI_POST, undefined, LiPostType.BY_TOPIC),
		],
	},
	{
		key: "competitor",
		tileIds: [
			getCatalogTileId(ListSourceType.LI_POST, undefined, LiPostType.BY_URL),
			getCatalogTileId(ListSourceType.LI_POST, undefined, LiPostType.BY_PROFILE),
		],
	},
	{
		key: "funding",
		tileIds: [getCatalogTileId(ListSourceType.FUNDING_ROUNDS, undefined, FundingMaType.FUNDING)],
	},
	{
		key: "followers",
		tileIds: [getCatalogTileId(ListSourceType.LI_COMPANY_ACTIVITY)],
	},
	{
		key: "engagement",
		tileIds: [
			getCatalogTileId(ListSourceType.LI_PROFILE_VISITS),
			getCatalogTileId(ListSourceType.LI_COMPANY_ACTIVITY),
			getCatalogTileId(ListSourceType.LI_POST, undefined, LiPostType.BY_URL),
			getCatalogTileId(ListSourceType.LI_POST, undefined, LiPostType.BY_PROFILE),
		],
	},
	{
		key: "hiring",
		tileIds: [getCatalogTileId(ListSourceType.JOBS, ScrapeLevel.GET_ENRICHMENT)],
	},
	{
		key: "leads",
		tileIds: [
			getCatalogTileId(ListSourceType.COMPANY_ENRICH, undefined, CompanyEnrichType.SEARCH),
			getCatalogTileId(ListSourceType.COMPANY_ENRICH, undefined, CompanyEnrichType.LOOK_A_LIKE),
			getCatalogTileId(
				ListSourceType.PARALLEL_AI,
				ScrapeLevel.GET_ENRICHMENT,
				ParallelAiType.SEARCH
			),
		],
	},
	{
		key: "signals",
		tileIds: [
			getCatalogTileId(ListSourceType.TRACKER, CsvEnrichmentType.PERSON),
			getCatalogTileId(ListSourceType.JOB_CHANGE, CsvEnrichmentType.PERSON),
		],
	},
];

/** Ordered tile ids for a chip, or `null` when the key is unknown / no chip is active. */
export const getExampleTileIds = (key: string | null): string[] | null => {
	if (!key) {
		return null;
	}

	return CATALOG_EXAMPLES.find((example) => example.key === key)?.tileIds ?? null;
};
